'use client';

import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../components/styled/text';
import { SmartValue } from './EntityBadge';
import { normalizeToolName } from '../utils/tool-config';
import { isMongoId, detectEntityType } from '../context/EntityResolverContext';
import { HttpRequestRenderer } from './HttpRequestRenderer';
import {
  isMcpToolName,
  isHttpExchangeTool,
} from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import {
  filterMcpDisplayArgs,
  hasCompactArgsPreview,
  truncateArgsPreview,
} from './tool-args-display.util';

export { hasCompactArgsPreview };

type ToolArgsRendererProps = {
  toolName: string;
  args: Record<string, unknown>;
  /** Compact mode - single line */
  compact?: boolean;
};

function getDisplayArgs({
  toolName,
  args,
}: {
  toolName: string;
  args: Record<string, unknown>;
}): Record<string, unknown> {
  return filterMcpDisplayArgs({ toolName, args });
}

function renderMcpBloodhoundArgsPreview({
  toolName,
  args,
}: {
  toolName: string;
  args: Record<string, unknown>;
}) {
  const normalized = normalizeToolName(toolName);

  if (normalized.includes('cypher') && typeof args.query === 'string') {
    return (
      <Text fontSize="xs" color="fg.subtle" fontFamily="mono">
        {truncateArgsPreview(args.query, 80)}
      </Text>
    );
  }

  if (normalized.includes('search') && typeof args.term === 'string') {
    return (
      <Text fontSize="xs" color="fg.subtle">
        {args.term}
      </Text>
    );
  }

  if (normalized.includes('graph') && typeof args.mode === 'string') {
    const center =
      typeof args.centerNodeId === 'string' ? ` · ${args.centerNodeId}` : '';

    return (
      <Text fontSize="xs" color="fg.subtle">
        {String(args.mode)}
        {center}
      </Text>
    );
  }

  if (typeof args.query === 'string') {
    return (
      <Text fontSize="xs" color="fg.subtle" fontFamily="mono">
        {truncateArgsPreview(args.query, 80)}
      </Text>
    );
  }

  if (typeof args.term === 'string') {
    return (
      <Text fontSize="xs" color="fg.subtle">
        {args.term}
      </Text>
    );
  }

  if (typeof args.mode === 'string') {
    const center =
      typeof args.centerNodeId === 'string' ? ` · ${args.centerNodeId}` : '';

    return (
      <Text fontSize="xs" color="fg.subtle">
        {String(args.mode)}
        {center}
      </Text>
    );
  }

  return null;
}

/**
 * Renders tool arguments with entity ID resolution
 * Uses SmartValue to automatically detect and replace entity IDs with badges
 */
export function ToolArgsRenderer({
  toolName,
  args,
  compact = true,
}: ToolArgsRendererProps) {
  const displayArgs = getDisplayArgs({ toolName, args });

  if (!displayArgs || Object.keys(displayArgs).length === 0) {
    return null;
  }

  const normalizedName = normalizeToolName(toolName);

  if (isMcpToolName(toolName)) {
    const mcpPreview = renderMcpBloodhoundArgsPreview({
      toolName,
      args: displayArgs,
    });

    if (mcpPreview) {
      return mcpPreview;
    }
  }

  if (isHttpExchangeTool(toolName)) {
    return <HttpRequestRenderer args={displayArgs} compact={compact} />;
  }

  switch (normalizedName) {
    case 'get-endpoint-details':
    case 'endpoint-details':
    case 'endpoint-details-tool':
    case 'get-tracked-endpoint-schema':
    case 'endpoint-read':
      if (displayArgs.endpointId) {
        return (
          <SmartValue fieldName="endpointId" value={displayArgs.endpointId} />
        );
      }

      break;

    case 'get-authentication-details':
    case 'authentication-details':
    case 'authentication-details-tool':
    case 'login-tool':
      if (displayArgs.credentialId) {
        return (
          <SmartValue
            fieldName="credentialId"
            value={displayArgs.credentialId}
          />
        );
      }

      break;

    case 'search-endpoints':
      if (displayArgs.query) {
        return (
          <Text fontSize="xs" color="fg.subtle">
            {String(displayArgs.query)}
          </Text>
        );
      }

      return null;

    case 'ask-codebase':
      if (displayArgs.question) {
        const question = String(displayArgs.question);
        const truncated =
          question.length > 90 ? `${question.slice(0, 90)}...` : question;

        return (
          <Text fontSize="xs" color="fg.subtle">
            {truncated}
          </Text>
        );
      }

      return null;

    case 'coder':
    case 'sub-agent-coder':
      if (displayArgs.query) {
        const query = String(displayArgs.query);
        const truncated =
          query.length > 60 ? `${query.slice(0, 60)}...` : query;

        return (
          <Text fontSize="xs" color="fg.subtle">
            {truncated}
          </Text>
        );
      }

      break;

    case 'sub-agent-intelligence':
    case 'navigate-web':
      if (displayArgs.query) {
        const query = String(displayArgs.query);
        const truncated =
          query.length > 60 ? `${query.slice(0, 60)}...` : query;

        return (
          <Text fontSize="xs" color="fg.subtle">
            {truncated}
          </Text>
        );
      }

      break;

    case 'business-context-tool':
    case 'get-business-context':
      if (displayArgs.query) {
        const query = String(displayArgs.query);
        const truncated =
          query.length > 80 ? `${query.slice(0, 80)}...` : query;

        return (
          <Text fontSize="xs" color="fg.subtle">
            {truncated}
          </Text>
        );
      }

      return null;

    case 'end-test':
    case 'todo-write':
    case 'ai-discovery-complete':
    case 'ai-discovery-verdict':
      return null;

    case 'get-neo-memories':
      if (displayArgs.query) {
        const query = String(displayArgs.query);
        const truncated =
          query.length > 80 ? `${query.slice(0, 80)}...` : query;

        return (
          <Text fontSize="xs" color="fg.subtle">
            {truncated}
          </Text>
        );
      }

      return null;

    case 'remote-c2-command':
      if (displayArgs.command) {
        return (
          <Text fontSize="xs" color="fg.subtle" fontFamily="mono">
            {String(displayArgs.command)}
          </Text>
        );
      }

      break;

    case 'list-ad-entities':
      if (displayArgs.entityKind) {
        return (
          <Text fontSize="xs" color="fg.subtle">
            {String(displayArgs.entityKind)}
          </Text>
        );
      }

      break;

    case 'query-ad-graph':
      if (displayArgs.mode) {
        const center =
          typeof displayArgs.centerNodeId === 'string'
            ? ` · ${displayArgs.centerNodeId}`
            : '';

        return (
          <Text fontSize="xs" color="fg.subtle">
            {String(displayArgs.mode)}
            {center}
          </Text>
        );
      }

      break;

    case 'get-ad-entity-details':
    case 'get-ad-entity-relationships':
      if (displayArgs.nodeId) {
        return (
          <Text fontSize="xs" color="fg.subtle" fontFamily="mono">
            {String(displayArgs.nodeId)}
          </Text>
        );
      }

      break;

    case 'create-internal-network-discovery-signal':
      if (displayArgs.description) {
        const description = String(displayArgs.description);
        const truncated =
          description.length > 80
            ? `${description.slice(0, 80)}...`
            : description;

        return (
          <Text fontSize="xs" color="fg.subtle">
            {truncated}
          </Text>
        );
      }

      break;
  }

  if (compact) {
    return <CompactArgsRenderer args={displayArgs} />;
  }

  return <DetailedArgsRenderer args={displayArgs} />;
}

/**
 * Compact renderer - shows entity badges inline
 */
function CompactArgsRenderer({ args }: { args: Record<string, unknown> }) {
  const entityFields: Array<{ key: string; value: string }> = [];

  // Find entity ID fields
  for (const [key, value] of Object.entries(args)) {
    if (isMongoId(value) && detectEntityType(key)) {
      entityFields.push({ key, value });
    }
  }

  if (entityFields.length === 0) {
    return null;
  }

  return (
    <HStack gap={1} alignItems="center" flexWrap="wrap">
      {entityFields.map(({ key, value }) => (
        <SmartValue key={key} fieldName={key} value={value} />
      ))}
    </HStack>
  );
}

/**
 * Detailed renderer - shows all args with labels
 */
function DetailedArgsRenderer({ args }: { args: Record<string, unknown> }) {
  const entries = Object.entries(args).filter(
    ([, value]) =>
      value !== undefined &&
      value !== null &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0),
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <VStack gap={1} alignItems="stretch">
      {entries.map(([key, value]) => (
        <HStack key={key} gap={2} alignItems="center">
          <Text fontSize="2xs" color="fg.muted" minW="80px">
            {formatFieldName(key)}:
          </Text>
          <Box flex={1}>
            <SmartValue
              fieldName={key}
              value={value}
              fallback={(val) => (
                <Text fontSize="xs" fontFamily="mono" color="fg.default">
                  {formatValue(val)}
                </Text>
              )}
            />
          </Box>
        </HStack>
      ))}
    </VStack>
  );
}

function formatFieldName(name: string): string {
  // Convert camelCase to Title Case
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.length > 50 ? `${value.slice(0, 50)}...` : value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `[${value.length} items]`;
  }

  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value).slice(0, 50) + '...';
  }

  return String(value);
}
